import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Button, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, Loaded, SourceChip } from './parts';
import { FUNNELS, INBOX_COPY, fleetTotals, funnel, headline, inbox, isStale, latestFullSweep, loginSummary, trend, worstDrop } from './model';

// One unit (bills), one axis. Categorical slots validated for CVD separation in both themes.
const SERIES = [['posted', 'Posted', '#2a78d6'], ['verified', 'Verified', '#1baf7a'], ['automated', 'Fully automated', '#eb6834']];
const duration = sweep => {
  const ms = Date.parse(sweep.finished_at) - Date.parse(sweep.started_at);
  if (!Number.isFinite(ms)) return null;
  const m = Math.round(ms / 60000);
  return m >= 60 ? "".concat(Math.floor(m / 60), "h ").concat(m % 60, "m") : "".concat(m, "m");
};

// A stat whose source is loading or failed says so instead of reading as zero.
const pending = state => state.loading ? 'loading' : state.error ? 'could not load' : '';
const Stat = _ref => {
  let label = _ref.label,
    value = _ref.value,
    hint = _ref.hint;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      minWidth: 120
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, label), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    sx: {
      fontVariantNumeric: 'tabular-nums'
    }
  }, typeof value === 'number' ? value.toLocaleString() : value), hint ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, hint) : null);
};

/** How close automation is to the team's manual month, and what needs a person today. */
export const OverviewView = _ref2 => {
  var _sweep$totals$ok, _sweep$totals, _sweep$totals$provide, _sweep$totals2, _sweep$totals$pdfs, _sweep$totals3, _sweep$totals$stamped, _sweep$totals4, _sweep$totals$quarant, _sweep$totals5, _sweep$registry$lengt, _sweep$registry;
  let health = _ref2.health,
    scoreboard = _ref2.scoreboard,
    credentials = _ref2.credentials,
    onNavigate = _ref2.onNavigate;
  const today = scoreboard.rows[0] || null;
  const sweep = latestFullSweep(health.rows);
  const head = headline(today);
  const funnels = today ? ['logins', 'bills'].map(which => ({
    which,
    stages: funnel(today, which)
  })) : [];
  // An all-clear is only true when every source answered with something. A failed load, or a sweep or scoreboard
  // that has not been published, must never read as "nothing to do". No logins at all is a real answer.
  const failed = [[credentials, 'logins', false], [health, 'sweep health', true], [scoreboard, 'held bills', true]].filter(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 3),
      state = _ref4[0],
      needsRows = _ref4[2];
    return state.error || needsRows && !state.loading && !state.rows.length;
  });
  const checking = credentials.loading || health.loading || scoreboard.loading;
  const items = inbox({
    credentials: credentials.rows,
    sweep,
    scoreboard: today
  });
  const series = trend(scoreboard.rows);
  const fleet = fleetTotals(health.rows);
  const logins = loginSummary(credentials.rows);
  const scoreAt = today === null || today === void 0 ? void 0 : today.generated_at;
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 16,
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 16
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 7
  }, /*#__PURE__*/React.createElement(Card, {
    title: "Verified scraped bills this month",
    source: today ? /*#__PURE__*/React.createElement(SourceChip, {
      prod: true,
      at: scoreAt
    }) : null,
    sx: {
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(Loaded, {
    state: scoreboard
  }, head ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3",
    sx: {
      fontVariantNumeric: 'tabular-nums'
    }
  }, head.verified.toLocaleString(), ' ', /*#__PURE__*/React.createElement(Typography, {
    component: "span",
    variant: "h5",
    color: "text.secondary"
  }, "of ".concat(head.baseline.toLocaleString()))), /*#__PURE__*/React.createElement(LinearProgress, {
    variant: "determinate",
    value: Math.min(100, head.pct),
    sx: {
      height: 10,
      borderRadius: 5,
      marginY: '10px'
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "".concat(head.pct, "% of the bills the team files in a month. Baseline: the last closed cycle, entitled companies."))) : /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "No scoreboard yet. It is written once a day at 18:00 UTC.")))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 5
  }, /*#__PURE__*/React.createElement(Card, {
    title: "Needs a person",
    sx: {
      height: '100%'
    }
  }, items.length ? /*#__PURE__*/React.createElement(Stack, {
    divider: /*#__PURE__*/React.createElement(Box, {
      sx: {
        borderBottom: 1,
        borderColor: 'divider'
      }
    })
  }, items.map(item => /*#__PURE__*/React.createElement(Stack, {
    key: item.kind,
    direction: "row",
    spacing: 12,
    alignItems: "center",
    sx: {
      paddingY: '6px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    sx: {
      minWidth: 40,
      fontVariantNumeric: 'tabular-nums'
    }
  }, item.n.toLocaleString()), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      flex: 1
    }
  }, INBOX_COPY[item.kind](item.n)), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButtonInvert",
    onClick: () => onNavigate(item)
  }, item.href ? 'Open Bill Queue' : 'Open')))) : null, items.length && checking ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      marginTop: '8px'
    }
  }, "Still checking the rest...") : null, !items.length && !failed.length ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, checking ? 'Checking...' : 'Nothing needs a person right now.') : null, failed.length ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginTop: items.length ? '12px' : 0
    }
  }, "Could not check ".concat(failed.map(_ref5 => {
    let _ref6 = _slicedToArray(_ref5, 2),
      what = _ref6[1];
    return what;
  }).join(', '), ". This list may be missing work.")) : null))), funnels.length ? /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 16
  }, funnels.map(_ref7 => {
    let which = _ref7.which,
      stages = _ref7.stages;
    const worst = worstDrop(stages);
    return /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      md: 6,
      key: which
    }, /*#__PURE__*/React.createElement(Card, {
      title: FUNNELS[which].title,
      source: /*#__PURE__*/React.createElement(SourceChip, {
        prod: true,
        at: scoreAt
      }),
      sx: {
        height: '100%'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      spacing: 10
    }, stages.map(stage => /*#__PURE__*/React.createElement(Box, {
      key: stage.key,
      sx: {
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '150px 1fr 110px'
        },
        gap: '4px 12px',
        alignItems: 'center'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, stage.label), /*#__PURE__*/React.createElement(LinearProgress, {
      variant: "determinate",
      value: stage.share,
      color: stage === worst ? 'error' : 'primary',
      sx: {
        height: 12,
        borderRadius: 4
      }
    }), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        fontVariantNumeric: 'tabular-nums'
      }
    }, stage.value.toLocaleString(), stage.drop ? /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      variant: "caption",
      color: "text.secondary"
    }, " (-".concat(stage.drop.toLocaleString(), ")")) : null)))), /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary",
      sx: {
        display: 'block',
        marginTop: '10px'
      }
    }, "".concat(worst ? "Biggest drop: ".concat(worst.drop.toLocaleString(), " at \"").concat(worst.label, "\". ") : '').concat(which === 'logins' ? 'Bars are a share of mapped utilities.' : "Bars are a share of the team's monthly bills."))));
  })) : null, series.length > 1 ? /*#__PURE__*/React.createElement(Card, {
    title: "This month, day by day"
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%',
      height: 240
    }
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, null, /*#__PURE__*/React.createElement(LineChart, {
    data: series,
    margin: {
      top: 8,
      right: 16,
      bottom: 0,
      left: 0
    }
  }, /*#__PURE__*/React.createElement(CartesianGrid, {
    strokeDasharray: "3 3",
    vertical: false
  }), /*#__PURE__*/React.createElement(XAxis, {
    dataKey: "date",
    tick: {
      fontSize: 12
    }
  }), /*#__PURE__*/React.createElement(YAxis, {
    allowDecimals: false,
    tick: {
      fontSize: 12
    }
  }), /*#__PURE__*/React.createElement(Tooltip, null), /*#__PURE__*/React.createElement(Legend, null), SERIES.map(_ref8 => {
    let _ref9 = _slicedToArray(_ref8, 3),
      key = _ref9[0],
      name = _ref9[1],
      color = _ref9[2];
    return /*#__PURE__*/React.createElement(Line, {
      key: key,
      type: "monotone",
      dataKey: key,
      name: name,
      stroke: color,
      strokeWidth: 2,
      dot: false,
      isAnimationActive: false
    });
  }))))) : null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 16
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 7
  }, /*#__PURE__*/React.createElement(Card, {
    title: "Last full sweep",
    source: sweep ? /*#__PURE__*/React.createElement(SourceChip, {
      prod: true,
      at: sweep.finished_at
    }) : null,
    sx: {
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(Loaded, {
    state: health
  }, sweep ? /*#__PURE__*/React.createElement(React.Fragment, null, isStale(sweep.finished_at) ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '12px'
    }
  }, "The last full sweep is more than 36 hours old. These numbers are not current.") : null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      marginBottom: '8px'
    }
  }, "Finished ".concat(new Date(sweep.finished_at).toLocaleString()).concat(duration(sweep) ? " \xB7 ".concat(duration(sweep)) : '', " \xB7 ").concat(sweep.mode)), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 24,
    flexWrap: "wrap",
    useFlexGap: true
  }, /*#__PURE__*/React.createElement(Stat, {
    label: "Providers OK",
    value: "".concat((_sweep$totals$ok = (_sweep$totals = sweep.totals) === null || _sweep$totals === void 0 ? void 0 : _sweep$totals.ok) !== null && _sweep$totals$ok !== void 0 ? _sweep$totals$ok : 0, " / ").concat((_sweep$totals$provide = (_sweep$totals2 = sweep.totals) === null || _sweep$totals2 === void 0 ? void 0 : _sweep$totals2.providers) !== null && _sweep$totals$provide !== void 0 ? _sweep$totals$provide : 0)
  }), /*#__PURE__*/React.createElement(Stat, {
    label: "PDFs",
    value: (_sweep$totals$pdfs = (_sweep$totals3 = sweep.totals) === null || _sweep$totals3 === void 0 ? void 0 : _sweep$totals3.pdfs) !== null && _sweep$totals$pdfs !== void 0 ? _sweep$totals$pdfs : 0
  }), /*#__PURE__*/React.createElement(Stat, {
    label: "Stamped",
    value: (_sweep$totals$stamped = (_sweep$totals4 = sweep.totals) === null || _sweep$totals4 === void 0 ? void 0 : _sweep$totals4.stamped) !== null && _sweep$totals$stamped !== void 0 ? _sweep$totals$stamped : 0
  }), /*#__PURE__*/React.createElement(Stat, {
    label: "Quarantined",
    value: (_sweep$totals$quarant = (_sweep$totals5 = sweep.totals) === null || _sweep$totals5 === void 0 ? void 0 : _sweep$totals5.quarantined) !== null && _sweep$totals$quarant !== void 0 ? _sweep$totals$quarant : 0
  }))) : /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "The scraper has not published a run yet. It publishes one at the end of each sweep.")))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 5
  }, /*#__PURE__*/React.createElement(Card, {
    title: "The fleet",
    sx: {
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 24,
    flexWrap: "wrap",
    useFlexGap: true
  }, /*#__PURE__*/React.createElement(Stat, {
    label: "Scrapers built",
    value: pending(health) ? '-' : (_sweep$registry$lengt = sweep === null || sweep === void 0 || (_sweep$registry = sweep.registry) === null || _sweep$registry === void 0 ? void 0 : _sweep$registry.length) !== null && _sweep$registry$lengt !== void 0 ? _sweep$registry$lengt : 0,
    hint: pending(health) || 'One Node fetcher per utility portal'
  }), /*#__PURE__*/React.createElement(Stat, {
    label: "Bills fetched",
    value: pending(health) ? '-' : fleet.pdfs,
    hint: pending(health) || "across ".concat(fleet.sweeps, " full sweeps")
  }), /*#__PURE__*/React.createElement(Stat, {
    label: "Logins",
    value: pending(credentials) ? '-' : logins.total,
    hint: pending(credentials) || "".concat(logins.active.toLocaleString(), " active")
  }))))));
};